import { useEffect, useMemo, useState } from "react";
import { dashFetch } from "@/api/client";
import { Card, Toolbar } from "@/components/Card";
import type { BillingPlan, BillingPlansEnvelope, CapabilityGroup, TenantRow } from "@/types/account";

type PlansResponse = {
  ok?: boolean;
  plans?: BillingPlansEnvelope;
  capabilityGroups?: CapabilityGroup[];
  error?: string;
};

function emptyPlan(): BillingPlan {
  return {
    id: "custom",
    name: "Custom",
    description: "Custom plan for a specific customer segment.",
    monthlyPriceUsd: 49,
    includedCredits: 2500,
    overageUsdPerCredit: 0.03,
    public: false,
    beta: false,
    sort: 50,
    ctaLabel: "Contact us",
    capabilities: {}
  };
}

function money(v: number | null): string {
  return v === null ? "Custom" : `$${Number(v || 0).toLocaleString("en-US")}`;
}

export default function PlansPanel({ sites, onSitePlanChange }: { sites: TenantRow[]; onSitePlanChange: () => Promise<void> }) {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [groups, setGroups] = useState<CapabilityGroup[]>([]);
  const [drafts, setDrafts] = useState<Record<string, BillingPlan>>({});
  const [activeId, setActiveId] = useState("");
  const [msg, setMsg] = useState("");
  const [siteMsg, setSiteMsg] = useState("");

  const active = activeId ? drafts[activeId] : null;

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0) || a.name.localeCompare(b.name)),
    [plans]
  );

  async function load() {
    setMsg("");
    const r = await dashFetch<PlansResponse>("/dashboard/api/account/billing-plans", { cache: "no-store" });
    if (!r?.ok || !r.plans) throw new Error(String(r?.error || "Failed to load plans"));
    const list = Array.isArray(r.plans.plans) ? r.plans.plans : [];
    setPlans(list);
    setDrafts(Object.fromEntries(list.map((p) => [p.id, { ...p, capabilities: { ...(p.capabilities || {}) } }])));
    setGroups(Array.isArray(r.capabilityGroups) ? r.capabilityGroups : []);
    setActiveId((prev) => (prev && list.some((p) => p.id === prev) ? prev : list[0]?.id || ""));
  }

  useEffect(() => {
    void load().catch((e) => setMsg(`Failed to load plans: ${String(e instanceof Error ? e.message : e)}`));
  }, []);

  function updatePlan(id: string, patch: Partial<BillingPlan>) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  function updateCapability(id: string, cap: string, enabled: boolean) {
    setDrafts((prev) => {
      const cur = prev[id];
      return { ...prev, [id]: { ...cur, capabilities: { ...(cur.capabilities || {}), [cap]: enabled } } };
    });
  }

  async function save(plan: BillingPlan) {
    setMsg("Saving plan...");
    const r = await dashFetch<PlansResponse>("/dashboard/api/account/billing-plans", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ plan })
    });
    if (!r?.ok) throw new Error(String(r?.error || "Save failed"));
    await load();
    setMsg("Plan saved.");
  }

  async function remove(id: string) {
    if (!confirm(`Delete plan ${id}? Sites cannot use this plan when it is deleted.`)) return;
    setMsg("Deleting plan...");
    const r = await dashFetch<PlansResponse>("/dashboard/api/account/billing-plans", {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id })
    });
    if (!r?.ok) throw new Error(String(r?.error || "Delete failed"));
    await load();
    setMsg("Plan deleted.");
  }

  async function assignSite(domain: string, planId: string) {
    setSiteMsg("Saving site plan...");
    const r = await dashFetch<{ ok?: boolean; error?: string }>("/dashboard/api/account/site-plan", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ domain, planId })
    });
    if (!r?.ok) throw new Error(String(r?.error || "Assignment failed"));
    await onSitePlanChange();
    setSiteMsg("Site plan updated.");
  }

  return (
    <div className="accountPlansGrid">
      <Card title="Plans">
        <Toolbar>
          <p className="muted" style={{ margin: 0, flex: 1 }}>
            Edit fixed pricing, included credits, overage pricing, and feature capabilities.
          </p>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              const p = emptyPlan();
              let n = 1;
              while (drafts[`${p.id}-${n}`]) n += 1;
              p.id = `${p.id}-${n}`;
              setDrafts((prev) => ({ ...prev, [p.id]: p }));
              setPlans((prev) => [...prev, p]);
              setActiveId(p.id);
            }}
          >
            + New plan
          </button>
        </Toolbar>

        <div className="planPills">
          {sortedPlans.map((p) => (
            <button
              key={p.id}
              type="button"
              className={"planPill" + (p.id === activeId ? " active" : "")}
              onClick={() => setActiveId(p.id)}
            >
              <span>{p.name}</span>
              <small>{money(p.monthlyPriceUsd)} · {p.includedCredits.toLocaleString("en-US")} credits</small>
            </button>
          ))}
        </div>

        {msg ? <p className="muted mono" style={{ fontSize: 11 }}>{msg}</p> : null}

        {active ? (
          <div className="planEditor">
            <div className="planEditorFields">
              <label>Plan ID<input className="mono" value={active.id} disabled /></label>
              <label>Name<input value={active.name} onChange={(e) => updatePlan(active.id, { name: e.target.value })} /></label>
              <label>Description<textarea value={active.description} onChange={(e) => updatePlan(active.id, { description: e.target.value })} /></label>
              <label>Monthly price USD<input type="number" min={0} value={active.monthlyPriceUsd ?? 0} onChange={(e) => updatePlan(active.id, { monthlyPriceUsd: Number(e.target.value) })} /></label>
              <label>Included credits<input type="number" min={0} value={active.includedCredits} onChange={(e) => updatePlan(active.id, { includedCredits: Number(e.target.value) })} /></label>
              <label>Overage USD / credit<input type="number" min={0} step={0.001} value={active.overageUsdPerCredit} onChange={(e) => updatePlan(active.id, { overageUsdPerCredit: Number(e.target.value) })} /></label>
              <label>Sort<input type="number" value={active.sort} onChange={(e) => updatePlan(active.id, { sort: Number(e.target.value) })} /></label>
              <label>CTA label<input value={active.ctaLabel || ""} onChange={(e) => updatePlan(active.id, { ctaLabel: e.target.value })} /></label>
              <label className="checkLine"><input type="checkbox" checked={active.public} onChange={(e) => updatePlan(active.id, { public: e.target.checked })} /> Public pricing page</label>
              <label className="checkLine"><input type="checkbox" checked={active.beta} onChange={(e) => updatePlan(active.id, { beta: e.target.checked })} /> Beta tester group</label>
            </div>

            <div className="capabilityGroups">
              {groups.map((g) => (
                <div key={g.id} className="capabilityGroup">
                  <h4>{g.label}</h4>
                  {g.capabilities.map((c) => (
                    <label key={c.id} className="capabilityCheck">
                      <input
                        type="checkbox"
                        checked={active.capabilities?.[c.id] === true}
                        onChange={(e) => updateCapability(active.id, c.id, e.target.checked)}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <Toolbar>
              <button type="button" className="btn primary" onClick={() => void save(active).catch((e) => setMsg(`Save failed: ${String(e instanceof Error ? e.message : e)}`))}>Save plan</button>
              <button type="button" className="btn danger" onClick={() => void remove(active.id).catch((e) => setMsg(`Delete failed: ${String(e instanceof Error ? e.message : e)}`))}>Delete</button>
            </Toolbar>
          </div>
        ) : null}
      </Card>

      <Card title="Website plan assignments">
        <p className="muted">Assign a pricing plan per website. Feature locks follow the selected plan.</p>
        {siteMsg ? <p className="muted mono" style={{ fontSize: 11 }}>{siteMsg}</p> : null}
        <div className="tableWrap">
          <table className="table mono">
            <thead><tr><th>Website</th><th>Current plan</th><th style={{ width: 220 }}>Assign</th></tr></thead>
            <tbody>
              {sites.map((s) => {
                const d = String(s.domain || "");
                return (
                  <tr key={d}>
                    <td>{d}</td>
                    <td>{s.planName || s.planId || "Beta tester"}</td>
                    <td>
                      <select
                        value={s.planId || "beta"}
                        onChange={(e) => void assignSite(d, e.target.value).catch((err) => setSiteMsg(`Save failed: ${String(err instanceof Error ? err.message : err)}`))}
                      >
                        {sortedPlans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
